/**
 * Which query params count as acquisition attribution. Shared by the proxy,
 * which writes the `attribution` cookie, and the logged-out layout, which
 * forwards params through the redirect to /access. Those two used to carry
 * separate copies of this list, and `cta` (the marketing-site CTA id) was in
 * neither, so no click could be tied to the card, header or tool that caused it.
 */
const ATTRIBUTION_KEYS = new Set([
  "ref",
  "gclid",
  "fbclid",
  "msclkid",
  "wbraid",
  "gbraid",
  "ttclid",
  "cta",
]);

export const isAttributionParam = (key: string): boolean =>
  key.startsWith("utm_") || ATTRIBUTION_KEYS.has(key);

/**
 * Picks attribution params from a query string. When at least one is present
 * and none is `ref`, `referer` fills `ref`. Pass the request referer through
 * `externalReferer` first, so only a third-party page can fill it.
 */
export const pickAttributionParams = (
  entries: Iterable<[string, string]>,
  referer?: string | null,
): Record<string, string> => {
  const picked: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (isAttributionParam(key) && !(key in picked)) {
      picked[key] = value;
    }
  }
  if (Object.keys(picked).length > 0 && !picked.ref && referer) {
    picked.ref = referer;
  }
  return picked;
};

/** Optogrid's own sites: the app and the marketing site, on .com and .io. */
const OPTOGRID_HOST = /(^|\.)optogrid\.(com|io)$/;

/**
 * The request referer, or null unless it is a third-party page. The
 * marketing-site forwarder already sends `ref` whenever the visitor had an
 * external referrer, so an Optogrid referer means they had none. A same-host
 * referer is the app's own navigation, such as an RSC fetch or a server action,
 * on whatever host it is deployed. Stored as `ref`, either would record a
 * self-referral as the visitor's source.
 */
export const externalReferer = (
  referer: string | null,
  requestHostname: string,
): string | null => {
  if (!referer) return null;
  try {
    const { hostname } = new URL(referer);
    return hostname === requestHostname || OPTOGRID_HOST.test(hostname)
      ? null
      : referer;
  } catch {
    return null;
  }
};
